// "What Platonic noticed": the few findings worth reading before anything else on the process
// page. They are observations within this process's own data — no benchmarks against other
// processes or teams. Fixed priority rules promote recurring returns and app handoffs, then
// time observations, then informational path variation. Scores are not statistical confidence.
// Both Overview and process detail use these rules; the panel only reads finding data.
import { caseLoops } from "./processCaseNotes";
import { median } from "./processDurationStats";
import {
  type ProcessEdge,
  type ProcessInstance,
  type StepTimeStats,
  caseElapsedSeconds,
  caseTimeSplit,
  END_NODE_ID,
  transitionWaitSeconds,
} from "./processGraphModel";

const NOTICED_LIMIT = 3;
const MIN_CASES = 3;

export type Noticed =
  /**
   * More wall-clock time goes to pauses between steps than to the steps themselves: `share` of it,
   * and what that does to a typical case, read as medians.
   */
  | { kind: "waits"; share: number; workSeconds: number; elapsedSeconds: number }
  | {
      kind: "handoffs";
      apps: [string, string];
      share: number;
      cases: number;
      total: number;
      switches: number;
    }
  | { kind: "time-sink"; stepId: string; share: number }
  | { kind: "branching"; stepId: string; nextSteps: number }
  | { kind: "rework"; stepId: string; share: number }
  | { kind: "spread"; maxSeconds: number; medianSeconds: number }
  | { kind: "dominant-path"; share: number; otherPaths: number }
  | { kind: "no-dominant-path"; share: number };

/**
 * How many cases share each set of steps, most common first. Order and repetition are left out
 * on purpose: desktop work interleaves freely, and grouping on exact sequences splits cases that
 * did the same work into near-singleton "paths".
 */
const stepSetSizes = (instances: ProcessInstance[]): number[] => {
  const sizes = new Map<string, number>();
  for (const instance of instances) {
    if (instance.steps.length === 0) continue;
    const key = [...new Set(instance.steps.map(step => step.nodeId))].sort().join("\u0000");
    sizes.set(key, (sizes.get(key) ?? 0) + 1);
  }
  return [...sizes.values()].sort((a, b) => b - a);
};

export const rankedNoticedFindings = ({
  instances,
  stepStats,
  edges,
}: {
  instances: ProcessInstance[];
  stepStats: StepTimeStats;
  edges: ProcessEdge[];
}): Array<{ finding: Noticed; score: number }> => {
  if (instances.length === 0) return [];
  const scored: Array<{ finding: Noticed; score: number }> = [];
  const add = (finding: Noticed, score: number) => scored.push({ finding, score });
  const durations = instances.map(instance => instance.source.avgDuration).sort((a, b) => a - b);

  const { workSeconds, waitSeconds } = caseTimeSplit(instances);
  const elapsed = instances.flatMap(instance => caseElapsedSeconds(instance) ?? []);
  const casesWithGaps = instances.filter(instance =>
    instance.steps.some((step, index) => {
      const next = instance.steps[index + 1];
      return next && (transitionWaitSeconds(step, next) ?? 0) >= 120;
    }),
  ).length;
  // A single overnight case should not make the whole process look delayed.
  if (
    casesWithGaps >= MIN_CASES &&
    casesWithGaps / instances.length >= 0.5 &&
    waitSeconds > workSeconds &&
    elapsed.length > 0
  ) {
    add(
      {
        kind: "waits",
        share: waitSeconds / (workSeconds + waitSeconds),
        workSeconds: median(durations),
        elapsedSeconds: median(elapsed.sort((a, b) => a - b)),
      },
      2 + waitSeconds / (workSeconds + waitSeconds),
    );
  }

  const sink = stepStats.ranked[0];
  const sinkShare = sink ? (stepStats.shareById.get(sink.id) ?? 0) : 0;
  // One step always takes the most time; it is a finding only once it takes a real slice.
  if (sink && stepStats.count > 1 && sinkShare >= 0.15) {
    add({ kind: "time-sink", stepId: sink.id, share: sinkShare }, 1 + sinkShare);
  }

  // Only real alternative next steps count, not ending the case or repeating this step.
  const outgoing = new Map<string, Set<string>>();
  for (const edge of edges) {
    if (edge.to === END_NODE_ID || edge.to === edge.from) continue;
    const next = outgoing.get(edge.from) ?? new Set<string>();
    next.add(edge.to);
    outgoing.set(edge.from, next);
  }
  const fork = stepStats.steps
    .map(node => ({ id: node.id, next: outgoing.get(node.id)?.size ?? 0 }))
    .sort((a, b) => b.next - a.next || a.id.localeCompare(b.id))[0];
  if (fork && fork.next >= 4) {
    add({ kind: "branching", stepId: fork.id, nextSteps: fork.next }, 0.8);
  }

  if (instances.length >= MIN_CASES) {
    const returns = new Map<string, number>();
    let looping = 0;
    for (const instance of instances) {
      const loops = caseLoops(instance);
      if (loops.count > 0) looping += 1;
      for (const stepId of new Set(loops.stepIds)) {
        returns.set(stepId, (returns.get(stepId) ?? 0) + 1);
      }
    }
    const loopShare = looping / instances.length;
    const [returnedTo] = [...returns].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
    if (returnedTo && looping >= MIN_CASES && loopShare >= 0.2) {
      add({ kind: "rework", stepId: returnedTo[0], share: loopShare }, 3 + loopShare);
    }

    const medianSeconds = median(durations);
    const maxSeconds = durations[durations.length - 1];
    if (instances.length >= 5 && medianSeconds > 0 && maxSeconds >= 3 * medianSeconds) {
      add(
        { kind: "spread", maxSeconds, medianSeconds },
        1 + Math.min(1, maxSeconds / medianSeconds / 10),
      );
    }

    const paths = stepSetSizes(instances);
    const topShare = paths.length > 0 ? paths[0] / instances.length : 0;
    if (topShare >= 0.5 && paths.length > 1) {
      add({ kind: "dominant-path", share: topShare, otherPaths: paths.length - 1 }, 0.5);
    } else if (paths.length > 0 && topShare < 0.2) {
      add({ kind: "no-dominant-path", share: topShare }, 0.5);
    }
  }

  const handoff = recurringHandoffs(instances);
  if (handoff) add(handoff, 3 + handoff.share);
  return scored.sort((a, b) => b.score - a.score);
};

const isTimeFinding = (finding: Noticed): boolean =>
  finding.kind === "waits" || finding.kind === "time-sink" || finding.kind === "spread";

/** Diversity is deliberate: one time observation leaves room for structural opportunities. */
export const noticedFindings = (input: Parameters<typeof rankedNoticedFindings>[0]): Noticed[] => {
  let hasTime = false;
  return rankedNoticedFindings(input)
    .filter(({ finding }) => {
      if (!isTimeFinding(finding)) return true;
      if (hasTime) return false;
      hasTime = true;
      return true;
    })
    .slice(0, NOTICED_LIMIT)
    .map(({ finding }) => finding);
};

/**
 * App attribution says which app a step mainly used, not the order of clicks within that step.
 * Only adjacent, attributed steps count; missing or mixed attribution breaks the chain.
 * A pair must recur at least twice per case in at least half the scope (and three cases).
 */
const recurringHandoffs = (
  instances: ProcessInstance[],
): Extract<Noticed, { kind: "handoffs" }> | null => {
  const pairs = new Map<string, { apps: [string, string]; counts: number[] }>();
  for (const instance of instances) {
    const apps = instance.steps.map(step => {
      const entries = Object.entries(step.appSeconds ?? {})
        .filter(([, seconds]) => seconds > 0)
        .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
      const top = entries[0];
      const total = entries.reduce((sum, [, seconds]) => sum + seconds, 0);
      return top && top[1] > total / 2 && top[1] >= Math.max(5, step.duration * 0.1)
        ? top[0]
        : null;
    });
    const counts = new Map<string, { apps: [string, string]; count: number }>();
    for (let i = 1; i < apps.length; i++) {
      const from = apps[i - 1];
      const to = apps[i];
      if (!from || !to || from === to) continue;
      const pair = [from, to].sort() as [string, string];
      const key = JSON.stringify(pair);
      const prior = counts.get(key);
      counts.set(key, { apps: pair, count: (prior?.count ?? 0) + 1 });
    }
    for (const [key, pair] of counts) {
      if (pair.count < 2) continue;
      const aggregate = pairs.get(key) ?? { apps: pair.apps, counts: [] };
      aggregate.counts.push(pair.count);
      pairs.set(key, aggregate);
    }
  }
  const top = [...pairs.values()].sort(
    (a, b) =>
      b.counts.length - a.counts.length ||
      JSON.stringify(a.apps).localeCompare(JSON.stringify(b.apps)),
  )[0];
  if (!top || top.counts.length < MIN_CASES || top.counts.length / instances.length < 0.5)
    return null;
  return {
    kind: "handoffs",
    apps: top.apps,
    share: top.counts.length / instances.length,
    cases: top.counts.length,
    total: instances.length,
    switches: median(top.counts.sort((a, b) => a - b)),
  };
};
